import React, { useState, useEffect } from 'react'
import { useData } from '../DataProvider'
import useTranslation from '../../shared/TranslationProvider'
import InterestSelect from '../InterestSelect'
import { Input } from '../../../lib/ui/input'
import { Textarea } from '../../../lib/ui/textarea'
import EmailPreferences from './EmailPreferences'
import { ArrowIcon } from './icons'
import Exports from './Exports'
import { Button } from '~/lib/ui/button'
import { getApi } from '~/util/api'
import { Loading } from '~/lib/ui/loading'
import { TemplateProvider } from '~/lib/pdf/TemplateProvider'
import {
  templates,
  templateStyle,
  templateSupportedStyles,
} from '../../ResumeBuilder/templates'

const TimelineSelect = ({
  showTimeline,
  timelineValues,
  desiredTimeline,
  setDesiredTimeline,
  trackerId,
  t,
}) => {
  const timelineKeys = Object.keys(timelineValues)
  if (!showTimeline) return null
  return (
    <div className="col-span-2 sm:col-span-1">
      <label>{t('desired_timeline')}</label>
      <select
        className="h-12 w-full rounded-[10px] border-input bg-input-background"
        name="user[tracker_attributes][desired_timeline]"
        value={desiredTimeline || ''}
        onChange={(e) => setDesiredTimeline(parseInt(e.target.value))}
      >
        {timelineKeys.map((timelineKey, idx) => (
          <option
            key={`timelinekey-${idx}`}
            value={timelineKey == 0 ? '' : timelineKey}
          >
            {timelineValues[timelineKey]}
          </option>
        ))}
      </select>
      <input
        type="hidden"
        name="user[tracker_attributes][id]"
        value={trackerId}
      />
    </div>
  )
}

const JobSeekingFields = ({
  interestIsJobSeeking,
  desiredJobTitle,
  setDesiredJobTitle,
  desiredIndustry,
  setDesiredIndustry,
  aboutYourself,
  setAboutYourself,
  desiredLocation,
  setDesiredLocation,
  showTimeline,
  timelineValues,
  desiredTimeline,
  setDesiredTimeline,
  trackerId,
  t,
}) => (
  <>
    {interestIsJobSeeking && (
      <div className="col-span-2 sm:col-span-1">
        <label>{t('step3.desired_title')}</label>
        <Input
          type="text"
          className="h-12 rounded-[10px]"
          name="user[candidate_profile_attributes][job_title]"
          value={desiredJobTitle}
          onChange={(e) => setDesiredJobTitle(e.target.value)}
        />
      </div>
    )}
    {interestIsJobSeeking && (
      <div className="col-span-2 sm:col-span-1">
        <label>{t('step3.desired_industry')}</label>
        <Input
          type="text"
          className="h-12 rounded-[10px]"
          name="user[candidate_profile_attributes][industry]"
          value={desiredIndustry}
          onChange={(e) => setDesiredIndustry(e.target.value)}
        />
      </div>
    )}
    {interestIsJobSeeking && (
      <TimelineSelect
        showTimeline={showTimeline}
        timelineValues={timelineValues}
        desiredTimeline={desiredTimeline}
        setDesiredTimeline={setDesiredTimeline}
        trackerId={trackerId}
        t={t}
      />
    )}
    {interestIsJobSeeking && (
      <LocationField
        desiredLocation={desiredLocation}
        setDesiredLocation={setDesiredLocation}
        t={t}
      />
    )}
    {interestIsJobSeeking && (
      <div className="col-span-2">
        <label>{t('step3.about_yourself')}</label>
        <Textarea
          type="text"
          className="!min-h-40 w-full border-input"
          name="user[candidate_profile_attributes][about_yourself]"
          value={aboutYourself || ''}
          onChange={(e) => setAboutYourself(e.target.value)}
        />
      </div>
    )}
  </>
)

const LocationField = ({ desiredLocation, setDesiredLocation, t }) => (
  <div className="col-span-2 sm:col-span-1">
    <label>{t('step3.desired_location')}</label>
    <Input
      type="text"
      className="h-12 rounded-[10px]"
      name="user[candidate_profile_attributes][location]"
      value={desiredLocation}
      onChange={(e) => setDesiredLocation(e.target.value)}
    />
  </div>
)

const WorkingForSelfFields = ({
  interestIsWorkingForSelf,
  desiredOpportunities,
  setDesiredOpportunities,
  desiredIndustry,
  setDesiredIndustry,
  t,
}) => (
  <>
    {interestIsWorkingForSelf && (
      <div className="col-span-2 sm:col-span-1">
        <label>{t('step3.desired_opportunities')}</label>
        <Input
          type="text"
          className="h-12 rounded-[10px]"
          name="user[candidate_profile_attributes][desired_opportunities]"
          value={desiredOpportunities}
          onChange={(e) => setDesiredOpportunities(e.target.value)}
        />
      </div>
    )}
    {interestIsWorkingForSelf && (
      <div className="col-span-2 sm:col-span-1">
        <label>{t('step3.desired_industry')}</label>
        <Input
          type="text"
          className="h-12 rounded-[10px]"
          name="user[candidate_profile_attributes][industry]"
          value={desiredIndustry}
          onChange={(e) => setDesiredIndustry(e.target.value)}
        />
      </div>
    )}
  </>
)

const RetirementFields = ({
  interestIsRetirement,
  retirementGoals,
  setRetirementGoals,
  t,
}) =>
  interestIsRetirement ? (
    <div className="col-span-2">
      <label>{t('step3.retirement_goals')}</label>
      <Textarea
        type="text"
        className="!min-h-40 w-full border-input"
        name="user[candidate_profile_attributes][retirement_goals]"
        value={retirementGoals || ''}
        onChange={(e) => setRetirementGoals(e.target.value)}
      />
    </div>
  ) : null

const RoleTypeSelect = ({
  lastRoleType,
  lastRoleTypeValues,
  setLastRoleType,
  t,
}) => (
  <div className="col-span-2 sm:col-span-1">
    <label>{t('step4.last_role_type.description')}</label>
    <select
      className="h-12 w-full rounded-[10px] border-input bg-input-background"
      name="user[candidate_profile_attributes][last_role_type]"
      value={lastRoleType}
      onChange={(e) => setLastRoleType(e.target.value)}
    >
      {lastRoleTypeValues.map(([id, _value]) => (
        <option key={id} value={id}>
          {t(`step4.last_role_type.${id}`)}
        </option>
      ))}
    </select>
  </div>
)

const SenioritySelect = ({
  seniorityLevel,
  seniorityLevels,
  setSeniorityLevel,
  t,
}) => (
  <div className="col-span-2 sm:col-span-1">
    <label>{t('step4.seniority.description')} </label>
    <select
      className="h-12 w-full rounded-[10px] border-input bg-input-background"
      name="user[seniority_level]"
      value={seniorityLevel}
      onChange={(e) => setSeniorityLevel(e.target.value)}
    >
      {seniorityLevels.map(([id, value]) => (
        <option key={id} value={value}>
          {t(`step4.seniority.${id}`)}
        </option>
      ))}
    </select>
  </div>
)

const EducationSelect = ({
  seekingEducation,
  seekingEducationValues,
  setSeekingEducation,
  t,
}) => (
  <div className="col-span-2 sm:col-span-1">
    <label>{t('step4.seeking_education_short')} </label>
    <select
      className="h-12 w-full rounded-[10px] border-input bg-input-background"
      name="user[candidate_profile_attributes][seeking_education]"
      value={seekingEducation}
      onChange={(e) => setSeekingEducation(e.target.value)}
    >
      {seekingEducationValues.map((value) => (
        <option key={value} value={value}>
          {t(`step4.education_${value}`)}
        </option>
      ))}
    </select>
  </div>
)

const SocialProfileFields = ({
  linkedInProfileLabel,
  linkedInProfile,
  setLinkedInProfile,
  twitterProfileLabel,
  twitterProfile,
  setTwitterProfile,
  facebookProfileLabel,
  facebookProfile,
  setFacebookProfile,
}) => (
  <>
    <div className="col-span-2 sm:col-span-1">
      <label>{linkedInProfileLabel}</label>
      <Input
        type="text"
        className="h-12 rounded-[10px]"
        name="user[networking_social_media_profile][linked_in_profile_url]"
        value={linkedInProfile}
        onChange={(e) => setLinkedInProfile(e.target.value)}
      />
    </div>
    <div className="col-span-2 sm:col-span-1">
      <label>{twitterProfileLabel}</label>
      <Input
        type="text"
        className="h-12 rounded-[10px]"
        name="user[networking_social_media_profile][twitter_profile_url]"
        value={twitterProfile}
        onChange={(e) => setTwitterProfile(e.target.value)}
      />
    </div>
    <div className="col-span-2 sm:col-span-1">
      <label>{facebookProfileLabel}</label>
      <Input
        type="text"
        className="h-12 rounded-[10px]"
        name="user[networking_social_media_profile][facebook_profile_url]"
        value={facebookProfile}
        onChange={(e) => setFacebookProfile(e.target.value)}
      />
    </div>
  </>
)

const ProfilePageSettingsInternal = ({
  showTimeline,
  trackerTimeline,
  trackerId,
  is_profile_page,
  candidateFirstName,
  emailPreferences,
  exportsData,
  coachWebinarEligible,
}) => {
  const {
    interest,
    desiredJobTitle,
    setDesiredJobTitle,
    desiredIndustry,
    setDesiredIndustry,
    aboutYourself,
    setAboutYourself,
    desiredOpportunities,
    setDesiredOpportunities,
    retirementGoals,
    setRetirementGoals,
    desiredLocation,
    setDesiredLocation,
    lastRoleType,
    lastRoleTypeValues,
    setLastRoleType,
    linkedInProfile,
    setLinkedInProfile,
    twitterProfile,
    setTwitterProfile,
    facebookProfile,
    setFacebookProfile,
    seniorityLevel,
    seniorityLevels,
    setSeniorityLevel,
    seekingEducation,
    seekingEducationValues,
    setSeekingEducation,
    interestIsRetirement,
    interestIsWorkingForSelf,
    interestIsJobSeeking,
    serverTranslations,
    timelineValues,
  } = useData()

  const { t } = useTranslation()
  const { linkedInProfileLabel, twitterProfileLabel, facebookProfileLabel } =
    serverTranslations

  const [desiredTimeline, setDesiredTimeline] = useState(trackerTimeline)
  const [isMinimized, setIsMinimized] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const mountTimeRef = React.useRef(Date.now())

  useEffect(() => {
    const allLoaded =
      lastRoleTypeValues !== undefined &&
      lastRoleTypeValues !== null &&
      lastRoleTypeValues.length > 0 &&
      seniorityLevels !== undefined &&
      seniorityLevels !== null &&
      seniorityLevels.length > 0 &&
      seekingEducationValues !== undefined &&
      seekingEducationValues !== null &&
      seekingEducationValues.length > 0 &&
      timelineValues !== undefined &&
      timelineValues !== null &&
      typeof timelineValues === 'object'

    if (allLoaded) {
      const elapsed = Date.now() - mountTimeRef.current
      const minDelay = 1000
      if (elapsed >= minDelay) {
        setIsLoading(false)
      } else {
        setTimeout(() => setIsLoading(false), minDelay - elapsed)
      }
    }
  }, [
    lastRoleTypeValues,
    seniorityLevels,
    seekingEducationValues,
    timelineValues,
  ])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await getApi().patch('/onboarding/', {
        social_media_profile: {
          linked_in_profile_url: linkedInProfile,
          twitter_profile_url: twitterProfile,
          facebook_profile_url: facebookProfile,
        },
        candidate: {
          seniority_level: seniorityLevel,
          candidate_profile_attributes: {
            job_title: desiredJobTitle,
            industry: desiredIndustry,
            location: desiredLocation,
            interest: interest,
            last_role_type: lastRoleType,
            about_yourself: aboutYourself,
            retirement_goals: retirementGoals,
            desired_opportunities: desiredOpportunities,
            seeking_education: seekingEducation,
          },
          tracker_attributes: {
            desired_timeline: desiredTimeline,
            id: trackerId,
          },
        },
      })
      setIsSaving(false)
    } catch (err) {
      console.error('Error saving profile:', err)
      setIsSaving(false)
    }
  }
  const defaultStyle = exportsData.template_settings
    ? {
        ...templateStyle,
        [`template-${exportsData.template_index}`]:
          exportsData.template_settings,
      }
    : templateStyle

  const templateProps = {
    templates,
    defaultStyle,
    templateSupportedStyles,
    template_index: exportsData.template_index,
  }

  return (
    <div className="p-5 sm:p-10">
      <div>
        <h1>
          <span className="font-normal">
            {t('MyAccountSettings.light_heading')}
          </span>{' '}
          {t('MyAccountSettings.dark_heading')}
        </h1>
        <p>
          {t('MyAccountSettings.hello_candidate', {
            name: candidateFirstName || '',
          })}
        </p>
      </div>
      <div className="mt-6 flex flex-col gap-8 sm:gap-8 xl:flex-row">
        <div className="w-full xl:w-3/4">
          <div
            className="h-fit rounded-[20px] border border-[#B9B9B9] p-4 sm:p-8"
            style={{ boxShadow: '0px 4px 4px 0px #99999930' }}
          >
            <div className="mb-4 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 sm:gap-y-6 md:gap-x-16">
              <div className="col-span-1 flex items-center justify-between sm:col-span-2">
                <h2 className="!text-xl font-normal">
                  {t('MyAccountSettings.basic_profile_information')}
                </h2>
                <button
                  type="button"
                  aria-label={
                    isMinimized
                      ? t('MyAccountSettings.expand')
                      : t('MyAccountSettings.minimize')
                  }
                  onClick={() => setIsMinimized((prev) => !prev)}
                  className={`ml-2 rounded p-1 transition-transform duration-200 hover:bg-gray-100 ${isMinimized ? 'rotate-180' : ''}`}
                >
                  <ArrowIcon />
                </button>
              </div>
              {!isMinimized && (
                <>
                  {isLoading ? (
                    <div className="col-span-2 flex justify-center">
                      <Loading className="mx-auto" size={6} />
                    </div>
                  ) : (
                    <>
                      <div className="col-span-2">
                        <label>{t('interested_in')}</label>
                        <InterestSelect is_profile_page={is_profile_page} />
                        <input
                          type="hidden"
                          name="user[candidate_profile_attributes][interest]"
                          value={[interest]}
                        />
                      </div>
                      <JobSeekingFields
                        interestIsJobSeeking={interestIsJobSeeking}
                        desiredJobTitle={desiredJobTitle}
                        setDesiredJobTitle={setDesiredJobTitle}
                        desiredIndustry={desiredIndustry}
                        setDesiredIndustry={setDesiredIndustry}
                        aboutYourself={aboutYourself}
                        setAboutYourself={setAboutYourself}
                        setDesiredLocation={setDesiredLocation}
                        desiredLocation={desiredLocation}
                        showTimeline={showTimeline}
                        timelineValues={timelineValues}
                        desiredTimeline={desiredTimeline}
                        setDesiredTimeline={setDesiredTimeline}
                        trackerId={trackerId}
                        t={t}
                      />
                      <WorkingForSelfFields
                        interestIsWorkingForSelf={interestIsWorkingForSelf}
                        desiredOpportunities={desiredOpportunities}
                        setDesiredOpportunities={setDesiredOpportunities}
                        desiredIndustry={desiredIndustry}
                        setDesiredIndustry={setDesiredIndustry}
                        t={t}
                      />
                      <RetirementFields
                        interestIsRetirement={interestIsRetirement}
                        retirementGoals={retirementGoals}
                        setRetirementGoals={setRetirementGoals}
                        t={t}
                      />
                      <SenioritySelect
                        seniorityLevel={seniorityLevel}
                        seniorityLevels={seniorityLevels}
                        setSeniorityLevel={setSeniorityLevel}
                        t={t}
                      />
                      <RoleTypeSelect
                        lastRoleType={lastRoleType}
                        lastRoleTypeValues={lastRoleTypeValues}
                        setLastRoleType={setLastRoleType}
                        t={t}
                      />
                      <EducationSelect
                        seekingEducation={seekingEducation}
                        seekingEducationValues={seekingEducationValues}
                        setSeekingEducation={setSeekingEducation}
                        t={t}
                      />
                      <SocialProfileFields
                        linkedInProfileLabel={linkedInProfileLabel}
                        linkedInProfile={linkedInProfile}
                        setLinkedInProfile={setLinkedInProfile}
                        twitterProfileLabel={twitterProfileLabel}
                        twitterProfile={twitterProfile}
                        setTwitterProfile={setTwitterProfile}
                        facebookProfileLabel={facebookProfileLabel}
                        facebookProfile={facebookProfile}
                        setFacebookProfile={setFacebookProfile}
                      />
                      <div className="col-span-1 mt-4 flex flex-col items-end sm:col-span-2">
                        <Button
                          type="button"
                          onClick={handleSave}
                          disabled={isSaving}
                          className="w-32"
                        >
                          {isSaving ? (
                            <Loading className="mx-auto" size={6} />
                          ) : (
                            t('save')
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="mt-8">
            <TemplateProvider {...templateProps}>
              <Exports exportsData={exportsData} />
            </TemplateProvider>
          </div>
        </div>
        <div className="w-full xl:w-1/4">
          <EmailPreferences
            emailPreferences={emailPreferences}
            coachWebinarEligible={coachWebinarEligible}
          />
        </div>
      </div>
    </div>
  )
}

export default ProfilePageSettingsInternal
